import { useEffect, useRef, useState, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';

/** Margine positivo in basso — animazione parte mentre ci avviciniamo alla sezione */
export const APPROACH_MARGIN = '0px 0px 45% 0px';

const EASE = [0.22, 1, 0.36, 1];
const SCRAMBLE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789—/·&';

const scramble = (text, chars = SCRAMBLE_CHARS) =>
  text
    .split('')
    .map((ch) => (ch === ' ' ? ' ' : chars[Math.floor(Math.random() * chars.length)]))
    .join('');

const useTextTrigger = ({
  once = true,
  amount = 0,
  margin = APPROACH_MARGIN,
  animateOnMount = false,
  externalInView,
}) => {
  const ref = useRef(null);
  const selfInView = useInView(ref, { once, amount, margin });
  const hasExternal = externalInView !== undefined;
  const active = Boolean(
    animateOnMount || (hasExternal ? externalInView : selfInView),
  );
  return { ref, active };
};

/* ── Blur split — parola/lettera entra morbida: blur + lift ── */
export const BlurSplitText = ({
  text,
  split = 'word',
  className = '',
  as: Tag = 'span',
  delay = 0,
  stagger = 0.05,
  duration = 0.58,
  blur = 14,
  y = 14,
  once = true,
  amount = 0,
  margin = APPROACH_MARGIN,
  animateOnMount = false,
  externalInView,
}) => {
  const { ref, active } = useTextTrigger({
    once,
    amount,
    margin,
    animateOnMount,
    externalInView,
  });

  const units =
    split === 'char'
      ? Array.from(text)
      : text.split(/(\s+)/).filter(Boolean);

  return (
    <Tag ref={ref} className={className} aria-label={text}>
      {units.map((unit, i) => (
        <motion.span
          key={`${unit}-${i}`}
          className="blur-split-unit"
          initial={{ opacity: 0, y }}
          animate={active ? { opacity: 1, y: 0 } : { opacity: 0, y }}
          transition={{
            duration,
            delay: delay + i * stagger,
            ease: EASE,
          }}
          aria-hidden={split === 'char'}
          style={{ display: 'inline-block', verticalAlign: 'bottom' }}
        >
          {unit === ' ' || unit === '\u00A0' ? '\u00A0' : unit}
        </motion.span>
      ))}
    </Tag>
  );
};

/* ── Scramble reveal — metadata monospace, risoluzione terminale ── */
export const ScrambleReveal = ({
  text,
  className = '',
  delay = 0,
  tickMs = 24,
  once = true,
  amount = 0,
  margin = APPROACH_MARGIN,
  animateOnMount = false,
  externalInView,
  chars = SCRAMBLE_CHARS,
}) => {
  const { ref, active } = useTextTrigger({
    once,
    amount,
    margin,
    animateOnMount,
    externalInView,
  });

  const [display, setDisplay] = useState(() => scramble(text, chars));
  const resolved = useRef(false);
  const runId = useRef(0);

  const reset = useCallback(() => {
    resolved.current = false;
    setDisplay(scramble(text, chars));
  }, [text, chars]);

  useEffect(() => {
    if (!active) {
      if (!once && !resolved.current) reset();
      return undefined;
    }
    if (resolved.current) return undefined;

    const id = ++runId.current;
    let frame = 0;
    const totalFrames = Math.max(Math.ceil(text.length * 1.75), 8);
    let timeoutId;

    const tick = () => {
      if (id !== runId.current) return;

      const progress = frame / totalFrames;
      setDisplay(
        text
          .split('')
          .map((ch, i) => {
            if (ch === ' ') return ' ';
            const threshold = i / text.length;
            if (progress >= threshold) return ch;
            return chars[Math.floor(Math.random() * chars.length)];
          })
          .join('')
      );

      frame += 1;
      if (frame <= totalFrames) {
        timeoutId = window.setTimeout(tick, tickMs);
      } else {
        setDisplay(text);
        resolved.current = true;
      }
    };

    timeoutId = window.setTimeout(tick, delay * 1000);
    return () => window.clearTimeout(timeoutId);
  }, [active, once, text, delay, tickMs, chars, reset]);

  return (
    <motion.span
      ref={ref}
      className={`scramble-reveal ${className}`}
      aria-label={text}
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? 1 : 0 }}
      transition={{ duration: 0.32, ease: EASE }}
    >
      {display}
    </motion.span>
  );
};

/* ── Clip-path reveal verticale — sipario che si alza ── */
export const ClipRevealText = ({
  text,
  children,
  className = '',
  as: _Tag = 'span',
  delay = 0,
  duration = 0.82,
  once = true,
  amount = 0,
  margin = APPROACH_MARGIN,
  animateOnMount = false,
  externalInView,
}) => {
  const { ref, active } = useTextTrigger({
    once,
    amount,
    margin,
    animateOnMount,
    externalInView,
  });

  const label = typeof text === 'string' ? text : undefined;

  return (
    <motion.span
      ref={ref}
      className={`clip-reveal-inner${className ? ` ${className}` : ''}`}
      aria-label={label}
      initial={{ opacity: 0 }}
      animate={{ opacity: active ? 1 : 0 }}
      transition={{ duration, delay, ease: EASE }}
    >
      {text ?? children}
    </motion.span>
  );
};
